/**
 * Profile validation utilities
 * Checks if user profile has all required fields completed
 */

import type { Profile } from '@/lib/api/services/profile';

/**
 * Checks if a profile has all required fields completed
 *
 * Required fields:
 * - first_name, last_name
 * - phone, country_code
 * - street_address, city, state, zip, country
 * - gender, date_of_birth
 * - preferred_language, group_id
 *
 * @param profile - The profile to check (can be Profile from API or any object with profile fields)
 * @returns true if all required fields are filled, false otherwise
 */
export function isProfileComplete(profile: any | null | undefined): boolean {
  if (!profile) return false;

  // Check all required fields are present and not empty
  const requiredFields: (keyof Profile)[] = [
    'first_name',
    'last_name',
    'phone',
    'country_code',
    'street_address',
    'city',
    'state',
    'zip',
    'country',
    'gender',
    'date_of_birth',
    'preferred_language',
    'group_id',
  ];

  for (const field of requiredFields) {
    const value = profile[field];

    // Check if field is missing, null, undefined, or empty string
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return false;
    }
  }

  return true;
}

/**
 * Gets a list of missing required fields from a profile
 * Useful for debugging or showing which fields need to be completed
 *
 * @param profile - The profile to check (can be Profile from API or any object with profile fields)
 * @returns Array of missing field names
 */
export function getMissingProfileFields(profile: any | null | undefined): string[] {
  if (!profile) {
    return ['all fields - no profile found'];
  }

  const requiredFields: (keyof Profile)[] = [
    'first_name',
    'last_name',
    'phone',
    'country_code',
    'street_address',
    'city',
    'state',
    'zip',
    'country',
    'gender',
    'date_of_birth',
    'preferred_language',
    'group_id',
  ];

  const missingFields: string[] = [];

  for (const field of requiredFields) {
    const value = profile[field];

    if (!value || (typeof value === 'string' && value.trim() === '')) {
      missingFields.push(field);
    }
  }

  return missingFields;
}
